/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import Tooltip from '../../../../form/fields/common/Tooltip';
import RichText from '../../../../richText';

import { Td } from './css';

import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';
import getFormattedPricing from '../../utils';
import { isCustomPricingCorporate as checkCustomPricingCorporate } from '../../../../utils/corporatePricing';
import { getTotalSmartDocks, checkGeoTabNewPricing, setGeoTabNewPrice, setGeoTabNewPriceTerm, setAdditionalRavenDevices } from '../util';

import {
  IotTerm3Year,
  IotRaven5CAN_USNon_Share,
  IotRaven5Non_Share,
  IotRaven5Non_Share_Price,
  IotRaven5CAN_USNon_Share_Price,
  IotTermMTM,
  IotVisionVideoDataAddOn,
  IotVisionVideoDataAddOn10GB,
  IotVisionVideoDataAddOn5GB,
  IotVisionVideoDataAddOn25GB
} from '../../../../utils/constant';

const RatePlanTableSubRow = ({
  hardware,
  orderType,
  productSelected,
  accountType,
  corporateType,
  rateplanName,
  deviceData,
  fiftyPercentInUS,
  viewType,
  ratePlans,
  setRatePlans,
  rateplanType,
  setGeoTabNewPriceValid,
  setHardware
}) => {
  const {
    fields: { deviceName, terms, toolTip }
  } = deviceData;
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: {
      valueCorporate,
      valueCorporateStandardPricing,
      valueBusinessRegularMedium,
      valueNorthStarSystemsFarmAndRanch,
      valueBusinessPersonal,
      valueBusinessRegular,
      threeYears,
      value2year,
      value3year,
      valueRenewal,
      valueMTM
    },
    iotOrderDetailsSection: { labelSelectTerm }
  } = localizedStrings;

  const isCustomPricingCorporate = checkCustomPricingCorporate(
    accountType,
    corporateType,
    { valueCorporate, valueCorporateStandardPricing }
  );
  /**
   * Raven 5 new-pricing override applies to Business Regular Medium and to
   * Corporate (both Custom and Standard pricing variants — Standard must
   * behave like BRM per product spec). Price visibility is still gated by
   * `isCustomPricingCorporate` below.
   */
  const geoTabPricingAccountTypes = [valueCorporate, valueBusinessRegularMedium];

  const [term, setTerm] = useState(() =>
    _.get(ratePlans, `${rateplanName}.${deviceName}.term`, '')
  );
  const [quantity, setQuantity] = useState(() =>
    _.get(ratePlans, `${rateplanName}.${deviceName}.quantity`, 0)
  );
  const [unitPrice, setUnitPrice] = useState(() =>
    _.get(ratePlans, `${rateplanName}.${deviceName}.unitPrice`, 0)
  );

  const rowRef = useRef(false);
  const isTermsFieldDisabled =
    productSelected === valueNorthStarSystemsFarmAndRanch &&
    rateplanName === 'TELUS Farm Inventory Management Software';
  const isQuantityFieldDisabled =
    productSelected === valueNorthStarSystemsFarmAndRanch &&
    rateplanName === 'TELUS Farm Inventory Management Software';

  const updateUnitPrice = () => {
    if (checkGeoTabNewPricing(productSelected, orderType, geoTabPricingAccountTypes, accountType, viewType, rateplanName, [IotTerm3Year], term)) {
      if (rateplanName === IotRaven5Non_Share) setUnitPrice(IotRaven5Non_Share_Price);
      else if (rateplanName === IotRaven5CAN_USNon_Share) setUnitPrice(IotRaven5CAN_USNon_Share_Price);
    } else {
      const selectedTermData = terms.filter(row => row.fields.termName === term);

      const unitPriceKey = fiftyPercentInUS
        ? 'termPrice50PrecentUs'
        : 'termPrice';
      const unitPriceValue = _.get(
        selectedTermData,
        `0.fields.${unitPriceKey}`,
        0
      );
      setUnitPrice(unitPriceValue);
    }

  };

  const getTerms = () => {
    let updatedTerms = [...terms];
    // Keep MTM available for the BYOD device on the Go Core rate plan
    const isGoCoreByod =
      typeof rateplanName === 'string' &&
      rateplanName.replace(/\s+/g, ' ').trim().toLowerCase() === 'go core' &&
      deviceName === 'BYOD';
    if ([valueBusinessPersonal, valueBusinessRegular].includes(accountType))
      updatedTerms = updatedTerms.filter(
        termItem => _.get(termItem, 'fields.termName') !== value3year
      );
    if (![valueBusinessPersonal, valueBusinessRegular].includes(accountType))
      updatedTerms = updatedTerms.filter(
        termItem => _.get(termItem, 'fields.termName') !== value2year
      );
    if (
      (viewType === 'simpleView' || orderType === valueRenewal) &&
      productSelected !== 'GeoTab' &&
      productSelected !== 'Geoforce'
    )
      updatedTerms = updatedTerms.filter(
        termItem => _.get(termItem, 'fields.termName') !== valueMTM
      );

    if (
      viewType === 'simpleView' &&
      orderType !== valueRenewal &&
      (productSelected === 'GeoTab' || productSelected === 'Geoforce') &&
      !isGoCoreByod
    )
      updatedTerms = updatedTerms.filter(
        termItem => _.get(termItem, 'fields.termName') !== valueMTM
      );
      if (
        orderType === valueRenewal &&
        rateplanName === IotVisionVideoDataAddOn &&
      ([IotVisionVideoDataAddOn10GB, IotVisionVideoDataAddOn5GB, IotVisionVideoDataAddOn25GB].includes(deviceName))
      )
      {
        updatedTerms = terms.filter(
          termItem => _.get(termItem, 'fields.termName') === valueMTM
        );
      }

    if ((productSelected === 'GeoTab' || productSelected === 'Geoforce') && orderType === valueRenewal)
      updatedTerms.forEach((termItem, index) => {
        if (_.get(termItem, 'fields.termName') === valueMTM) {
          updatedTerms[index].fields.termName = 'BYOD';
        }
      });
    return updatedTerms;
  };

  useEffect(() => {
    updateUnitPrice();
    if (!term) setQuantity(0);
  }, [term, fiftyPercentInUS]);

  useEffect(() => {
    if (checkGeoTabNewPricing(productSelected, orderType, geoTabPricingAccountTypes, accountType, viewType, rateplanName)) {
      if ([IotTermMTM, IotTerm3Year].includes(term)) {
        if (quantity > 0) {
          setGeoTabNewPriceValid(true);
        } else {
          setGeoTabNewPrice(rateplanName, ratePlans, setGeoTabNewPriceValid)
        }
      }
      setGeoTabNewPriceTerm(rateplanName, ratePlans, setGeoTabNewPriceValid, term);
    }
  }, [term, quantity]);

  useEffect(() => {
    if (checkGeoTabNewPricing(productSelected, orderType, geoTabPricingAccountTypes, accountType, viewType, rateplanName)) {
      setAdditionalRavenDevices(rateplanName, quantity, setHardware, term);
    }
  }, [quantity, term]);

  useEffect(() => {
    setRatePlans(previousValue => ({
      ...previousValue,
      [rateplanName]: {
        ...ratePlans[rateplanName],
        [deviceName]: {
          term,
          quantity,
          unitPrice,
          fiftyPercentInUS,
          rateplanType
        }
      }
    }));
  }, [term, quantity, unitPrice, fiftyPercentInUS]);

  useEffect(() => {
    if (
      productSelected === valueNorthStarSystemsFarmAndRanch &&
      rateplanName === 'TELUS Farm Inventory Management Software'
    ) {
      const totalSmartDockQuantity = getTotalSmartDocks(hardware);
      const valueTerm = totalSmartDockQuantity ? threeYears : '';
      setTerm(valueTerm);
      setQuantity(totalSmartDockQuantity);
    }
  }, [hardware]);

  useEffect(() => {
    if (viewType && rowRef.current) {
      setTerm('');
      setQuantity(0);
      setUnitPrice(0);
      setGeoTabNewPriceValid(false);
    }
    rowRef.current = true;
  }, [viewType]);

  useEffect(() => {
    if (_.isEmpty(ratePlans)) {
      setTerm('');
      setQuantity(0);
      setUnitPrice(0);
    }
  }, [ratePlans]);

  return (
    <>
      <Td>
        <select
          onChange={e => setTerm(e.target.value)}
          value={term}
          disabled={isTermsFieldDisabled}
        >
          <option value="">{labelSelectTerm}</option>
          {getTerms().map(termItem => {
            return (
              <option
                key={_.get(termItem, 'sys.id', '')}
                value={_.get(termItem, 'fields.termName')}
              >
                {_.get(termItem, 'fields.termName')}
              </option>
            );
          })}
        </select>
      </Td>
      <Td>
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          {deviceName}
          {toolTip && (
            <Tooltip copy="en"><RichText data={toolTip} /></Tooltip>
          )}
        </div>
      </Td>
      <Td>
        <input
          disabled={term === '' || isQuantityFieldDisabled}
          style={{ background: term ? '#fff' : '#f3f3f3' }}
          className="frg-input-field color-light"
          min={0}
          max={
            rateplanName === IotVisionVideoDataAddOn &&
            [
              IotVisionVideoDataAddOn10GB,
              IotVisionVideoDataAddOn5GB,
              IotVisionVideoDataAddOn25GB
            ].includes(deviceName)
              ? 999
              : undefined
          }
          value={quantity}
          onChange={e => {
            const value = +e.target.value;
            if (
              rateplanName === IotVisionVideoDataAddOn &&
              [
                IotVisionVideoDataAddOn10GB,
                IotVisionVideoDataAddOn5GB,
                IotVisionVideoDataAddOn25GB
              ].includes(deviceName) &&
              value > 999
            ) {
              setQuantity(0);
            } else {
              setQuantity(+e.target.value);
            }
          }}
        />
      </Td>
      {!isCustomPricingCorporate && (
        <>
          <Td>{getFormattedPricing(unitPrice)}</Td>
          <Td>{getFormattedPricing(unitPrice * quantity)}</Td>
        </>
      )}
    </>
  );
};

RatePlanTableSubRow.propTypes = {
  hardware: PropTypes.shape({}).isRequired,
  orderType: PropTypes.string.isRequired,
  productSelected: PropTypes.string.isRequired,
  accountType: PropTypes.string.isRequired,
  corporateType: PropTypes.string,
  rateplanName: PropTypes.string.isRequired,
  deviceData: PropTypes.shape({
    fields: PropTypes.shape({
      deviceName: PropTypes.string,
      terms: PropTypes.arrayOf(
        PropTypes.shape({
          termName: PropTypes.string,
          termPrice: PropTypes.number,
          termPrice50PrecentUs: PropTypes.number
        })
      )
    })
  }).isRequired,
  fiftyPercentInUS: PropTypes.bool.isRequired,
  viewType: PropTypes.string.isRequired,
  ratePlans: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setRatePlans: PropTypes.func.isRequired,
  rateplanType: PropTypes.string.isRequired
};

export default RatePlanTableSubRow;
